import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAssertedParams } from '@folio/lib';
import { getBranchDisplayName, getPolicyBranchPath } from '@folio/utils/branchDisplayName';
import { Badge } from '../../ui/badge';
import { useSuspenseQueries } from '@tanstack/react-query';
import {
  type BranchDto,
  branchDtoBranchTypeEnum,
  customerControllerGetCustomer,
  policyControllerGetPolicy,
} from 'korint-sdk';
import { EyeIcon, RefreshCwIcon, SplitIcon } from 'lucide-react';
import { Button, buttonVariants } from '../../ui/button/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip/tooltip';
import { PolicyStatus } from '../../features/PolicyStatus';
import { Skeleton } from '../../ui/skeleton/skeleton';
import { Table, TableBody, TableCell, TableRow } from '../../ui/table/table';
import { formatDateWithTime, formatPrice } from '../../../lib';
import { Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

export function getOpenBranchesForExplorer(
  branches: BranchDto[],
  isPolicyIssued: boolean,
): BranchDto[] {
  const targetType = isPolicyIssued
    ? branchDtoBranchTypeEnum.MTA
    : branchDtoBranchTypeEnum.NEW_BUSINESS;

  return branches
    .filter(branch => branch.branchStatus === 'BRANCH_OPEN' && branch.branchType === targetType)
    .sort((a, b) => new Date(b.lastModifiedAt).getTime() - new Date(a.lastModifiedAt).getTime());
}

function BranchExplorerPopoverContent({
  branches,
  isPolicyIssued,
  onNavigate,
}: {
  branches: BranchDto[];
  isPolicyIssued: boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const { policy } = usePolicyOnDashboard();
  const { branchId } = useAssertedParams();
  const location = useLocation();
  const customerInformations = usePolicyProductConfig('customerInformations');
  const customerFieldsConfig = customerInformations?.fieldsConfig;

  const results = useSuspenseQueries({
    queries: branches.map(branch => ({
      queryKey: ['policy', policy.id, branch.id],
      queryFn: async () => ({
        policy: await policyControllerGetPolicy(policy.id, { branchId: branch.id }),
        ...(policy.customers?.[0] && {
          customer: await customerControllerGetCustomer(policy.customers[0], {
            branchId: branch.id,
          }),
        }),
      }),
    })),
  });

  return results.map(({ data: { policy: policyOnBranch, customer: customerOnBranch } }, index) => {
    const branch = branches[index];

    const label = isPolicyIssued
      ? getBranchDisplayName(branch, t) // for MTAs
      : customerDisplayName(customerOnBranch ? [customerOnBranch] : [], customerFieldsConfig); // for quotes

    return (
      <TableRow
        key={`${policyOnBranch.id}-${branch.id}`}
        className={branch.id === branchId ? 'bg-muted/50' : undefined}
      >
        <TableCell className="max-w-72 break-words">
          {label ? (
            label
          ) : (
            <div className="italic text-muted-foreground">{t('branchExplorer.unavailable')}</div>
          )}
          {branch.id === branchId && (
            <Badge variant="outline" className="ml-2">
              {t('branchContext.currentProject')}
            </Badge>
          )}
        </TableCell>
        <TableCell>
          <PolicyStatus policy={policyOnBranch} workingBranch={branch} />
        </TableCell>
        <TableCell>
          {policyOnBranch.proratedQuote?.totalPremium.amountWithFeesAndTaxes ? (
            formatPrice(policyOnBranch.proratedQuote.totalPremium.amountWithFeesAndTaxes)
          ) : (
            <div className="italic text-muted-foreground">{t('branchExplorer.unavailable')}</div>
          )}
        </TableCell>
        <TableCell>{formatDateWithTime(new Date(branch.createdAt))}</TableCell>
        <TableCell>
          <Link
            className={buttonVariants({ variant: 'ghost', size: 'sm' })}
            to={getPolicyBranchPath({
              policyId: policyOnBranch.id,
              currentBranchId: branchId,
              targetBranchId: branch.id,
              ...location,
            })}
            onClick={onNavigate}
            aria-current={branch.id === branchId ? 'page' : undefined}
          >
            {t('branchExplorer.view')} <EyeIcon className="ml-2 h-4 w-4" aria-hidden="true" />
          </Link>
        </TableCell>
      </TableRow>
    );
  });
}

export function BranchExplorer({ triggerLabel }: { triggerLabel?: string } = {}) {
  const { t } = useTranslation();
  const { policyOnMain, branches, mutate: refresh } = usePolicyOnDashboard();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [open, setOpen] = useState(false);

  const isPolicyIssued = !['POLICY_CREATED', 'POLICY_QUOTED', 'POLICY_CONFIRMED'].includes(
    policyOnMain.status,
  );

  const branchesToDisplay = getOpenBranchesForExplorer(branches.data, isPolicyIssued);

  if (branchesToDisplay.length === 0 && !triggerLabel) {
    return null;
  }

  const label = isPolicyIssued ? t('branchExplorer.exploreMta') : t('branchExplorer.exploreQuotes');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {triggerLabel ? (
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            {triggerLabel}
          </Button>
        </PopoverTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="default" size="icon" aria-label={label}>
                <SplitIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      )}
      <PopoverContent align="end" className="w-auto max-w-[calc(100vw-2rem)]">
        <Title order={2} size="sm" className="mb-3">
          {label}
        </Title>
        {branchesToDisplay.length === 0 ? (
          <Stack align="start" gap={3} className="max-w-sm">
            <Text size="sm" tone="muted">
              {t('branchExplorer.noOpenProjects')}
            </Text>
            <Button
              variant="outline"
              size="sm"
              loading={isRefreshing}
              onClick={async () => {
                setIsRefreshing(true);
                try {
                  await refresh();
                } finally {
                  setIsRefreshing(false);
                }
              }}
            >
              <RefreshCwIcon className="mr-2 h-4 w-4" aria-hidden="true" />
              {t('branchExplorer.refresh')}
            </Button>
          </Stack>
        ) : (
          <Table>
            <TableBody>
              <Suspense
                fallback={
                  <>
                    <TableRow>
                      <TableCell>
                        <Skeleton className="w-[668px] h-8" />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Skeleton className="w-[668px] h-8" />
                      </TableCell>
                    </TableRow>
                  </>
                }
              >
                <BranchExplorerPopoverContent
                  branches={branchesToDisplay}
                  isPolicyIssued={isPolicyIssued}
                  onNavigate={() => setOpen(false)}
                />
              </Suspense>
            </TableBody>
          </Table>
        )}
      </PopoverContent>
    </Popover>
  );
}
